/**
 * PropiedadOwnershipPanel — panel "Propietario y Cliente" del Modal de
 * propiedad. Extraído de propiedades.tsx en round 2026-04-27 (Fase 8).
 *
 * QUÉ HACE
 *   1. Select multi para "Propietario(s)" — dueños legales de la propiedad.
 *      Incluye usuarios con rol "propietario" (sin login, restituido
 *      2026-05-01) Y los legacy con rol "cliente" + `nombre_propietario`
 *      (creados antes de que el rol existiera). Fix D2 (2026-06-20): antes
 *      este filtro excluía a los de rol "propietario" → un dueño legal real
 *      no aparecía como opción.
 *   2. Select single para "Cliente" — el dueño actual de la propiedad
 *      (rol "cliente"). Es OBLIGATORIO. Al cambiar a otro cliente
 *      cuando ya había uno previo, pide confirmación (evita reasignar
 *      por accidente).
 *   3. Botón "Crear nuevo usuario" inline en cada Select que abre el
 *      CreateUserModal del padre con el target apropiado.
 *
 * POR QUÉ ESTÁ AQUÍ Y NO INLINE
 *   - El bloque tenía ~100 LOC con dropdownRender + handler de
 *     reemplazo de cliente con modal de confirmación.
 *   - Las acciones de "abrir CreateUserModal" se delegan al padre vía
 *     onCreateOwner / onCreateClient — el panel no maneja la apertura,
 *     solo el trigger.
 *
 * REGLAS DE NEGOCIO RELEVANTES (ver docs/auth-roles.md):
 *   - Toda propiedad DEBE tener un cliente (id_cliente NOT NULL en DB).
 *   - Puede tener 0..N propietarios (relación many-to-many vía
 *     `propietarios_propiedades`); el cliente y los propietarios
 *     pueden coincidir o no.
 *   - Reemplazar cliente es operación reversible pero implica
 *     actualizar el dashboard y permisos del cliente saliente —
 *     por eso pedimos confirmación explícita.
 */

import React from "react";
import { Button, Divider, Form, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd";
import { getModal } from "../../../utils/antdStatic";
import { formatUserLabel } from "../../../utils/userDisplay";

// ─── Tipos auxiliares ───────────────────────────────────────────────────

/** Forma de un usuario para el Select. Mantenemos `any` adicional
 *  porque el catálogo de users en el padre es heterogéneo (viene del
 *  endpoint `/users` con campos opcionales no tipados estrictamente). */
interface UserOpt {
  id: number;
  rol: string;
  nombre_usuario?: string;
  nombre_propietario?: string;
  // Round 2026-05-09: el endpoint /users/clients-and-owners devuelve este
  // array para que el label del Select muestre "Nombre — PROPIEDAD"
  // (ver utils/userDisplay.ts). Opcional para no romper builds donde
  // el endpoint aún no haya deployado.
  propiedades?: string[];
  [k: string]: any;
}

interface PropiedadOwnershipPanelProps {
  /** Form instance del Modal padre — para `setFieldValue` en cancel del
   *  modal de confirmación (revertir cambio de cliente). */
  form: FormInstance;
  /** Lista de usuarios del catálogo. Filtramos rol="cliente" adentro. */
  users: UserOpt[];
  /** Propiedad en edición — null en modo crear. Necesario para
   *  detectar si HABÍA un cliente previo y pedir confirmación al
   *  cambiarlo. */
  editing: { idCliente?: number | null } | null;
  /** Trigger para abrir el CreateUserModal del padre con target=owner.
   *  El padre es quien renderiza el modal y maneja el alta. */
  onCreateOwner: () => void;
  /** Trigger para abrir el CreateUserModal del padre con target=client. */
  onCreateClient: () => void;
}

// ─── Componente ─────────────────────────────────────────────────────────

const PropiedadOwnershipPanel: React.FC<PropiedadOwnershipPanelProps> = ({
  form,
  users,
  editing,
  onCreateOwner,
  onCreateClient,
}) => {
  return (
    <div className="flex-container">
      <Form.Item label="Propietario(s)" name="propietarioIds">
        <Select
          mode="multiple"
          showSearch
          optionFilterProp="label"
          placeholder="Selecciona propietario(s)"
          options={users
            // Fix D2 (2026-06-20): dueños legales = rol "propietario" (sin
            // login, restituido 2026-05-01) O los legacy con rol "cliente" +
            // nombre_propietario (creados antes de que el rol existiera).
            // Antes solo el segundo grupo → los propietario-rol no aparecían.
            .filter(
              (u) =>
                u.rol === "propietario" ||
                (u.rol === "cliente" && u.nombre_propietario),
            )
            // Round 2026-05-09: label "Nombre — PROPIEDAD" via helper
            // compartido. Usamos nombre_propietario (el nombre del dueño
            // legal) con fallback a nombre_usuario por si falta.
            .map((u) => ({
              value: u.id,
              label: formatUserLabel({
                nombre: u.nombre_propietario ?? u.nombre_usuario,
                propiedades: u.propiedades,
              }),
            }))}
          allowClear
          // maxTagCount=1: si elegís varios propietarios, mostramos
          // solo el primero como tag visible y el resto como "+N más"
          // — evita romper el layout horizontal del flex-container.
          maxTagCount={1}
          maxTagPlaceholder={(omitted) => `+${omitted.length} más`}
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: "8px 0" }} />
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={onCreateOwner}
              >
                Crear nuevo usuario
              </Button>
            </>
          )}
        />
      </Form.Item>

      <Form.Item
        label="Cliente"
        name="idCliente"
        rules={[
          { required: true, message: "El cliente (dueño) es obligatorio" },
        ]}
      >
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Selecciona cliente"
          options={users
            .filter((u) => u.rol === "cliente")
            // Round 2026-05-09: label "Nombre — PROPIEDAD" igual que en
            // el select de Propietario(s) arriba. Acá usamos
            // nombre_usuario porque es el dueño "operativo" (el que
            // tiene login y vive en /finanzas-cliente).
            .map((u) => ({
              value: u.id,
              label: formatUserLabel({
                nombre: u.nombre_usuario,
                propiedades: u.propiedades,
              }),
            }))}
          allowClear
          onChange={(value) => {
            const prevId = editing?.idCliente;
            // Solo pedimos confirmación si HABÍA un cliente previo
            // distinto del nuevo. Editar la propiedad y elegir el
            // mismo cliente (o ninguno → cliente) no necesita prompt.
            if (prevId && value && value !== prevId) {
              const prevName =
                users.find((u) => u.id === prevId)?.nombre_usuario ??
                `#${prevId}`;
              getModal()?.confirm({
                title: "Reemplazar cliente",
                content: `Esta propiedad ya tiene el cliente ${prevName}. ¿Desea reemplazarlo?`,
                okText: "Sí, reemplazar",
                cancelText: "Cancelar",
                // En cancel revertimos al cliente previo. No hay onOk
                // explícito porque AntD ya escribió el nuevo valor en
                // el form antes de llegar acá.
                onCancel: () => {
                  form.setFieldValue("idCliente", prevId);
                },
              });
            }
          }}
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: "8px 0" }} />
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={onCreateClient}
              >
                Crear nuevo usuario
              </Button>
            </>
          )}
        />
      </Form.Item>
    </div>
  );
};

export default PropiedadOwnershipPanel;
